import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import {
  WHATSAPP_PRICING_MARKETS,
  WHATSAPP_RATE_CATEGORIES,
  type WhatsappPricingMarket,
  type WhatsappRate,
  type WhatsappRateCategory,
} from '@neuroedge/shared';
import { Button, Card, Modal, PageState, Select, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useCreateWhatsappRate,
  useDeleteWhatsappRate,
  useWhatsappRates,
} from '../../api/whatsapp-usage';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const MARKET_LABEL: Record<string, string> = Object.fromEntries(
  WHATSAPP_PRICING_MARKETS.map((m) => [m.code, m.label]),
);
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  WHATSAPP_RATE_CATEGORIES.map((c) => [c.category, c.label]),
);
const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
const inr = (v: number) => `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 4 })}`;

const STATE_STYLE: Record<WhatsappRate['state'], { label: string; color: string }> = {
  CURRENT: { label: 'In force', color: 'var(--color-success)' },
  SCHEDULED: { label: 'Scheduled', color: 'var(--color-accent-amber)' },
  ENDED: { label: 'Ended', color: 'var(--color-text-muted)' },
};

/**
 * Admin → WhatsApp rates: what Meta charges per delivered message, per kind
 * and recipient market. The workspaces' usage panel estimates their Meta
 * bill from these (Meta bills them itself). A new rate replaces the one in
 * force from its start date - history is kept.
 */
export function WhatsappRatesPage() {
  const { isPlatformAdmin } = useAuth();
  const rates = useWhatsappRates(isPlatformAdmin);
  const remove = useDeleteWhatsappRate();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [showEnded, setShowEnded] = useState(false);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  if (rates.isPending) return <PageState kind="loading" message="Loading rates…" />;
  if (rates.isError)
    return (
      <PageState kind="error" message={rates.error.message} onRetry={() => void rates.refetch()} />
    );

  const shown = rates.data.filter((r) => showEnded || r.state !== 'ENDED');
  const markets = WHATSAPP_PRICING_MARKETS.filter((m) => shown.some((r) => r.market === m.code));

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 960 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>WhatsApp rates</h1>
        <span style={{ flex: 1 }} />
        <label style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showEnded}
            onChange={(e) => setShowEnded(e.target.checked)}
          />
          Show ended
        </label>
        <Button onClick={() => setAdding(true)}>+ Add rate</Button>
      </div>
      <p style={{ ...muted, margin: 0 }}>
        Meta&apos;s price per delivered message in ₹, before GST, by kind and the recipient&apos;s
        country. Workspaces see their estimated Meta bill from these on Billing → WhatsApp usage.
        Countries without their own rate use <strong>Other countries (fallback)</strong>; with
        neither, messages are counted but not priced. When Meta changes a price, add the new rate
        from its start date - the old one ends then.
      </p>
      {markets.length === 0 ? (
        <Card>
          <p style={{ ...muted, margin: 0 }}>No rates yet.</p>
        </Card>
      ) : (
        markets.map((m) => (
          <Card key={m.code} title={m.label}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ ...muted, textAlign: 'left' }}>
                  <th style={{ fontWeight: 500 }}>Kind</th>
                  <th style={{ fontWeight: 500, textAlign: 'right' }}>Per message</th>
                  <th style={{ fontWeight: 500 }}>From</th>
                  <th style={{ fontWeight: 500 }}>Until</th>
                  <th style={{ fontWeight: 500 }}>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown
                  .filter((r) => r.market === m.code)
                  .map((r) => (
                    <tr key={r.id}>
                      <td>{CATEGORY_LABEL[r.category] ?? r.category}</td>
                      <td style={{ textAlign: 'right' }}>{inr(r.amount)}</td>
                      <td>{day(r.effectiveFrom)}</td>
                      <td>{r.effectiveTo ? day(r.effectiveTo) : '-'}</td>
                      <td>
                        <span
                          className="ne-badge"
                          style={{
                            borderColor: STATE_STYLE[r.state].color,
                            color: STATE_STYLE[r.state].color,
                          }}
                        >
                          {STATE_STYLE[r.state].label}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {r.state === 'SCHEDULED' ? (
                          <Button
                            variant="secondary"
                            disabled={remove.isPending}
                            onClick={() => {
                              if (!window.confirm('Remove this scheduled rate?')) return;
                              remove.mutate(r.id, {
                                onSuccess: () => toast.success('Scheduled rate removed.'),
                                onError: (err) => toast.error(err.message),
                              });
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Card>
        ))
      )}
      {adding ? <AddRateModal onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function AddRateModal({ onClose }: { onClose: () => void }) {
  const create = useCreateWhatsappRate();
  const toast = useToast();
  const [category, setCategory] = useState<WhatsappRateCategory | ''>('');
  const [market, setMarket] = useState<WhatsappPricingMarket | ''>('');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!category) e.category = 'Choose the kind of message.';
    if (!market) e.market = 'Choose the market.';
    const n = Number(amount);
    if (!amount.trim()) e.amount = 'Enter the price per message.';
    else if (!Number.isFinite(n) || n < 0) e.amount = 'Enter 0 or more.';
    else if (n > 100) e.amount = 'At most ₹100 per message.';
    if (!effectiveFrom) e.effectiveFrom = 'Choose the date it starts.';
    return e;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      setError('Please fix the highlighted fields.');
      return;
    }
    try {
      await create.mutateAsync({
        category: category as WhatsappRateCategory,
        market: market as WhatsappPricingMarket,
        amount: Number(amount),
        effectiveFrom,
      });
      toast.success('Rate added.');
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const server = fieldErrorsFromApiError(err);
        if (Object.keys(server).length) {
          setErrors(server);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not add the rate.');
    }
  }

  return (
    <Modal title="Add a WhatsApp rate" onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-3)' }}
      >
        <Select
          label="Kind"
          value={category}
          onChange={(e) => setCategory(e.target.value as WhatsappRateCategory)}
          options={[
            { value: '', label: 'Choose…' },
            ...WHATSAPP_RATE_CATEGORIES.map((c) => ({ value: c.category, label: c.label })),
          ]}
          error={errors.category}
        />
        <Select
          label="Market (recipient's country)"
          value={market}
          onChange={(e) => setMarket(e.target.value as WhatsappPricingMarket)}
          options={[
            { value: '', label: 'Choose…' },
            ...WHATSAPP_PRICING_MARKETS.map((m) => ({ value: m.code, label: m.label })),
          ]}
          error={errors.market}
        />
        <TextField
          label="Price per message (₹, before GST)"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={errors.amount}
        />
        <TextField
          label="Starts on (00:00 UTC)"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          error={errors.effectiveFrom}
        />
        <span style={muted}>The rate in force for this kind and market ends on this date.</span>
        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add rate'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
